import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Clock, ChevronLeft, ChevronRight, Loader2, CheckCircle, Video, MapPin, Users, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { isConferenceLocation, joinButtonLabel, locationLabel } from '../components/schedule/locationOptions';
import { calendarErrorNotice } from '../lib/calendarWarnings';
import { errorText } from '../lib/apiErrors';
import CoachAvatar from '../components/booking/CoachAvatar';
import CoachPicker from '../components/booking/CoachPicker';
import SelectionBar from '../components/booking/SelectionBar';
import TimezoneSelect from '../components/TimezoneSelect';
import { browserTimeZone, formatInZone, naiveInZone, zoneLabelAt } from '../lib/timezone';
import { safeHttpUrl } from '../lib/bookingManage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// #539 / F06: the confirmation "When:" line derives directly from the NAIVE
// slot string (guest wall-clock, no offset) plus the booking's persisted
// guest_timezone as its label. It never routes the string through new Date(),
// which would re-interpret it browser-local and mislabel it for every guest
// outside the viewer's zone. `zoneLabel` is optional: a pre-#539 response
// without guest_timezone still renders (unlabeled), and a non-string or
// malformed slot string falls back to the raw value rather than lying.
// #791 E31: `zoneLabel` is the guest-facing `zoneLabelAt` string, which already
// carries its own "(GMT+9)", so it is appended after a space, not wrapped in
// parentheses: "Thursday, September 10, 2026 at 9:00 AM (GMT+9) Tokyo (JST)".
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function whenLineText(naiveStart, zoneLabel) {
    if (!naiveStart || typeof naiveStart !== 'string') return String(naiveStart ?? '');
    const datePart = naiveStart.slice(0, 10);
    const hhmm = naiveStart.slice(11, 16);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (!m || !hhmm) return naiveStart; // non-naive shape → render verbatim
    const [, y, mo, d] = m;
    const weekday = WEEKDAYS[new Date(Date.UTC(+y, +mo - 1, +d)).getUTCDay()];
    const [hStr, mStr] = hhmm.split(':');
    const h = parseInt(hStr, 10);
    if (isNaN(h) || h > 23) return naiveStart;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const zone = typeof zoneLabel === 'string' && zoneLabel.trim() ? ` ${zoneLabel.trim()}` : '';
    return `${weekday}, ${MONTHS[+mo - 1]} ${+d}, ${y} at ${h12}:${mStr} ${ampm}${zone}`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── #579: the host's window belongs to the LINK's timezone ─────────────────
// The backend anchors `availability.start_hour..end_hour` to
// `linkData.timezone` and renders slots in whatever zone the visitor asks for.
// These helpers give the page the same picture: what the host published (to
// label it) and which visitor-local dates can hold any of those instants (to
// enable the right calendar cells when the conversion crosses midnight).

const pad2 = (n) => String(n).padStart(2, '0');

function clockLabel(hour, minute) {
    const h = Number(hour) || 0;
    const m = Number(minute) || 0;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${pad2(m)} ${ampm}`;
}

/** "9:00 AM – 5:00 PM" for the link's configured hours (in the link's zone). */
export function hostWindowLabel(availability) {
    const a = availability || {};
    return `${clockLabel(a.start_hour ?? 9, a.start_min ?? 0)} – ${clockLabel(a.end_hour ?? 17, a.end_min ?? 0)}`;
}

function shiftDay(dayStr, days) {
    const [y, m, d] = dayStr.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

// 0=Mon..6=Sun, matching the backend's availability.days.
function weekdayMon0(dayStr) {
    const [y, m, d] = dayStr.split('-').map(Number);
    return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

/**
 * Can the visitor-local calendar date `dayStr` (in `viewTz`) contain at
 * least one slot of a link whose window is authored in `linkTz`?
 *
 * A Manila 09:00–17:00 Tuesday is New York Monday 21:00 → Tuesday 05:00, so
 * a New York visitor's Monday holds Tuesday-host slots and their Sunday may
 * hold Monday-host slots. The three neighbouring host dates are projected
 * and checked for a slot START inside the visitor's day. Weekdays are the
 * HOST's (availability.days is the link's calendar).
 */
export function visitorDayHasHostSlots(dayStr, availability, linkTz, viewTz, durationMin = 30) {
    const a = availability || {};
    const allowed = a.days || [0, 1, 2, 3, 4];
    const dayStart = Date.parse(naiveInZone(`${dayStr}T00:00`, viewTz));
    const dayEnd = Date.parse(naiveInZone(`${shiftDay(dayStr, 1)}T00:00`, viewTz));
    if (Number.isNaN(dayStart) || Number.isNaN(dayEnd)) return false;
    const dur = Math.max(Number(durationMin) || 30, 1) * 60000;
    for (const offset of [-1, 0, 1]) {
        const hostDay = shiftDay(dayStr, offset);
        if (!allowed.includes(weekdayMon0(hostDay))) continue;
        const start = Date.parse(naiveInZone(`${hostDay}T${pad2(a.start_hour ?? 9)}:${pad2(a.start_min ?? 0)}`, linkTz));
        const end = Date.parse(naiveInZone(`${hostDay}T${pad2(a.end_hour ?? 17)}:${pad2(a.end_min ?? 0)}`, linkTz));
        if (Number.isNaN(start) || Number.isNaN(end)) continue;
        const lastSlotStart = end - dur;
        if (lastSlotStart < start) continue;
        // Any slot start within [dayStart, dayEnd)?
        if (start < dayEnd && lastSlotStart >= dayStart) return true;
    }
    return false;
}

/** A calendar cell's `Date` as the "YYYY-MM-DD" label the API speaks. Hoisted
 * (module scope) so every hook below can call it without a temporal dead zone. */
function formatDateStr(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// #758: the guest coach picker's copy, in one place.
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const shortDate = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const slotFreeCoaches = (slot) => (Array.isArray(slot?.free_coaches) ? slot.free_coaches : []);

/**
 * #758: one row per public coach card, derived from the guest's current
 * selection (the issue's selection table):
 *
 *   - a time is chosen → free iff the slot's `free_coaches` has the coach
 *     ("Free at 10:00 AM" / "Busy at 10:00 AM"); free coaches sort first;
 *   - a date is chosen (no time, slots loaded) → the coach's count of that
 *     day's slots ("3 times"), 0 → "Not on this day";
 *   - a date is chosen but its slots are still loading (`daySlots` null) →
 *     every coach row `pending` ("Checking times…", not pickable yet), so no
 *     coach is picked for a day they may have no times on;
 *   - nothing chosen → every coach available, no hint (Q1: no "Next:" hint).
 *
 * Coach order is kept otherwise, so the list never jumps under the pointer.
 * The sort is stable, so ties keep coach order.
 */
export function deriveCoachRows(coaches, { slot, dateChosen, daySlots, timeLabel }) {
    const cards = coaches || [];
    if (slot) {
        const free = slotFreeCoaches(slot);
        const rows = cards.map((c) => {
            const available = free.includes(c.coach_profile_id);
            return { id: c.coach_profile_id, available, hint: `${available ? 'Free' : 'Busy'} at ${timeLabel}` };
        });
        rows.sort((a, b) => Number(b.available) - Number(a.available));
        return { rows, anyHint: `${free.length} of ${cards.length} free at ${timeLabel}` };
    }
    if (dateChosen && !daySlots) {
        return {
            rows: cards.map((c) => ({ id: c.coach_profile_id, available: false, pending: true, hint: 'Checking times…' })),
            anyHint: '',
        };
    }
    if (dateChosen) {
        const rows = cards.map((c) => {
            const n = daySlots.filter((s) => slotFreeCoaches(s).includes(c.coach_profile_id)).length;
            return { id: c.coach_profile_id, available: n > 0, hint: n > 0 ? plural(n, 'time', 'times') : 'Not on this day' };
        });
        return { rows, anyHint: plural(daySlots.length, 'time', 'times') };
    }
    return { rows: cards.map((c) => ({ id: c.coach_profile_id, available: true, hint: '' })), anyHint: '' };
}

export default function PublicBooking() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = searchParams.get('embed') === 'true';

    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Calendar state
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    // Slots state
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // #756: which dates of the shown month hold any bookable time, from the
    // server (`/days`: the coaches' real hours, calendars, bookings and caps).
    // Until it answers — or when it fails — the client estimate below is used.
    const [serverDays, setServerDays] = useState(() => new Set());
    // #758: which coach (null = Any) `serverDays` answers for, so a coach's
    // calendar is never drawn from the Any answer (or vice versa).
    const [serverDaysCoach, setServerDaysCoach] = useState(null);
    const [daysStatus, setDaysStatus] = useState('idle'); // idle | loading | ready | failed

    // #539/#579: the zone the guest VIEWS slots in — the browser's, overridable
    // below. Since #579 it is purely a display zone: the host's window is
    // anchored to the link's own timezone on the server and converted for the
    // guest, so the same instants are offered whatever zone is chosen here.
    // (Issue #579 recorded "open in the link's zone"; the product decision was
    // to open in the visitor's, with the host's hours labelled alongside —
    // change the initializer to `linkData?.timezone` to flip it.) Slot strings
    // stay naive ISO in this zone — selection is by exact string identity.
    const [tz, setTz] = useState(() => browserTimeZone());

    // Booking form
    const [step, setStep] = useState('calendar'); // calendar | form | confirmed
    const [formData, setFormData] = useState({ name: '', email: '', notes: '' });
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    // #758: the guest's chosen coach (a public `coach_profile_id`, or null =
    // "Any available coach"), and a counter bumped to refetch the day's slots
    // and the month's days after a 409 `coach_unavailable`.
    const [coach, setCoach] = useState(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    // After a 409 coach_unavailable: the server's message, shown in the picker
    // layout and describing the coach list, which takes keyboard focus
    // (`focusPicker`) once the calendar step is back on screen.
    const [coachNotice, setCoachNotice] = useState('');
    const [focusPicker, setFocusPicker] = useState(false);
    const chipsPickerRef = useRef(null);
    const listPickerRef = useRef(null);
    // The picker renders only when the link has public coach cards: a
    // round-robin link with the guest-choice flag on (server-decided).
    const coaches = linkData?.coaches;
    const picker = Array.isArray(coaches) && coaches.length > 0;

    // Load booking link data
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/booking/public/${slug}`);
                setLinkData(res.data);
            } catch {
                setError('Booking page not found');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [slug]);

    // Load slots when date is selected. #758: no `coach` param — with the
    // picker on each slot carries `free_coaches` and the page filters on the
    // client, so a coach toggle needs no request. `alive` (C9.5) drops a late
    // answer for a date or zone the guest has already left.
    useEffect(() => {
        if (!selectedDate) return undefined;
        let alive = true;
        const loadSlots = async () => {
            setSlotsLoading(true);
            setSlots([]);
            try {
                const dateStr = formatDateStr(selectedDate);
                const res = await axios.get(
                    `${API}/booking/public/${slug}/slots?date=${dateStr}&timezone=${encodeURIComponent(tz)}`
                );
                if (alive) setSlots(res.data.slots || []);
            } catch { if (alive) toast.error('Failed to load available times'); }
            finally { if (alive) setSlotsLoading(false); }
        };
        loadSlots();
        return () => { alive = false; };
    }, [selectedDate, slug, tz, refreshNonce]);

    // #756: server-truth days for the shown month (C9.5 — above the early
    // returns, year/month derived from `currentMonth` here). `alive` drops a
    // late answer for a month or zone the guest has already left. A response
    // without a `days` array counts as a failure, so the page degrades to the
    // client estimate instead of a dead calendar.
    useEffect(() => {
        if (!linkData) return undefined;
        let alive = true;
        const y = currentMonth.getFullYear();
        const m = currentMonth.getMonth();
        const from = formatDateStr(new Date(y, m, 1));
        const to = formatDateStr(new Date(y, m + 1, 0));
        setDaysStatus('loading');
        // #758: a chosen coach narrows the month to that coach's days.
        const params = { from, to, timezone: tz };
        if (coach) params.coach = coach;
        axios.get(`${API}/booking/public/${slug}/days`, { params })
            .then(res => {
                if (!alive) return;
                const days = res?.data?.days;
                if (!Array.isArray(days)) { setDaysStatus('failed'); return; }
                setServerDays(new Set(days));
                setServerDaysCoach(coach || null);
                setDaysStatus('ready');
            })
            .catch(() => { if (alive) setDaysStatus('failed'); });
        return () => { alive = false; };
    }, [linkData, slug, tz, currentMonth, coach, refreshNonce]);

    // #758 (audit F08): after a 409 coach_unavailable the form unmounts under
    // the keyboard; move focus to the coach list that is on screen (the chips
    // below 1024px, the column above), which the notice describes.
    useEffect(() => {
        if (!focusPicker || step !== 'calendar') return;
        setFocusPicker(false);
        const shown = [listPickerRef.current, chipsPickerRef.current]
            .find((el) => el && el.getClientRects().length > 0);
        const target = shown || chipsPickerRef.current || listPickerRef.current;
        if (target) target.focus();
    }, [focusPicker, step]);

    // Label a slot for the chosen zone. #579 slots carry `start_utc` (the
    // instant) — format THAT in `tz`, so switching the selector moves the
    // numbers. A slot without it (older backend) falls back to the #539
    // rule: label the naive string ("2026-03-16T09:00:00", wall-clock IN the
    // chosen zone) straight from the string — never via new Date()/toLocale*,
    // which would re-label it browser-local.
    const formatSlotLabel = useCallback((slot) => {
        const startUtc = slot && typeof slot === 'object' ? slot.start_utc : null;
        if (startUtc) {
            const viaZone = formatInZone(startUtc, { zone: tz, formatStr: 'h:mm a' });
            if (viaZone) return viaZone;
        }
        const naiveStart = slot && typeof slot === 'object' ? slot.start : slot;
        if (!naiveStart || typeof naiveStart !== 'string') return '';
        const hhmm = naiveStart.slice(11, 16);
        if (!hhmm) return '';
        const [hStr, mStr] = hhmm.split(':');
        const h = parseInt(hStr, 10);
        if (isNaN(h)) return '';
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12}:${mStr} ${ampm}`;
    }, [tz]);

    // ── #758: derived selection state (useMemo only — no setState in render or
    // effects; C9.5: every hook sits above the early returns below). ─────────
    // The times the list shows: the day's slots, narrowed to the chosen coach.
    // Same array, so `slot.start` identity (#579) survives a coach toggle.
    const visibleSlots = useMemo(
        () => (coach ? slots.filter((s) => slotFreeCoaches(s).includes(coach)) : slots),
        [slots, coach],
    );
    const coachRows = useMemo(() => deriveCoachRows(coaches, {
        slot: selectedSlot,
        dateChosen: !!selectedDate,
        daySlots: slotsLoading ? null : slots,
        timeLabel: selectedSlot ? formatSlotLabel(selectedSlot) : '',
    }), [coaches, selectedSlot, selectedDate, slots, slotsLoading, formatSlotLabel]);
    const chosenCoach = useMemo(
        () => (coach ? (coaches || []).find((c) => c.coach_profile_id === coach) || null : null),
        [coaches, coach],
    );

    // ── #758: selection rules (handlers, never effects). ─────────────────────
    // A coach can only be picked while the chosen date/time is valid for them
    // (unavailable rows are aria-disabled), so no selection needs re-finding.
    const pickCoach = (id) => { setCoach(id || null); setCoachNotice(''); };
    const chooseSlot = (slot) => { setSelectedSlot(slot); setCoachNotice(''); };
    const removeChoice = (kind) => {
        // Removing the coach keeps date and time: the time was offered to that
        // coach, so it is offered to Any. A time belongs to a date.
        if (kind === 'coach') setCoach(null);
        if (kind === 'date') { setSelectedDate(null); setSelectedSlot(null); }
        if (kind === 'time') setSelectedSlot(null);
    };
    const resetChoices = () => { setCoach(null); setSelectedDate(null); setSelectedSlot(null); setCoachNotice(''); };
    // Slot strings are naive wall clocks in `tz`: a time kept across a zone
    // change would POST the old zone's string with the new zone. So a zone
    // change clears the time (and keeps coach and date).
    const changeTz = (value) => { setTz(value); setSelectedSlot(null); };

    const handleBook = async () => {
        if (!formData.name || !formData.email) return toast.error('Name and email are required');
        if (!selectedSlot) return;
        setSubmitting(true);
        try {
            // #539 / F06: send the zone the guest actually VIEWED slots in (the
            // `tz` state — browser default or their manual override). A fresh
            // browser read here would silently diverge from the override, so
            // the backend would pair the override's wall-clock slot with the
            // wrong zone (wrong instant on the host calendar, wrong email
            // rendering, wrong organizer label).
            const res = await axios.post(`${API}/booking/public/${slug}`, {
                name: formData.name,
                email: formData.email,
                start: selectedSlot.start,
                end: selectedSlot.end,
                answers: { ...answers, notes: formData.notes },
                timezone: tz,
                ...(coach ? { coach_profile_id: coach } : {}),
            });
            setConfirmation(res.data);
            setStep('confirmed');
        } catch (err) {
            // #758 (C8.2): the chosen coach is no longer free (or no longer
            // choosable). Back to the calendar with the time cleared, and
            // refetch the day's slots and the month's days.
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409 && detail?.code === 'coach_unavailable') {
                setSelectedSlot(null);
                setStep('calendar');
                setRefreshNonce((n) => n + 1);
                setCoachNotice(errorText(err, 'That coach is no longer available. Pick another time or coach.'));
                setFocusPicker(true);
                // Audit F09: refetch the coach cards, so a coach who was paused
                // or removed (or a flag turned off) disappears; a chosen coach
                // who is no longer a card falls back to "Any".
                const chosenAtSubmit = coach;
                axios.get(`${API}/booking/public/${slug}`)
                    .then((r) => {
                        const data = r?.data;
                        if (!data) return;
                        setLinkData(data);
                        const cards = Array.isArray(data.coaches) ? data.coaches : [];
                        if (chosenAtSubmit && !cards.some((c) => c.coach_profile_id === chosenAtSubmit)) setCoach(null);
                    })
                    .catch(() => {});
            }
            // errorText: a `detail` object/array must never be a React child (#603) —
            // e.g. the house {code, message} 409 (coach_unavailable, booking_busy_retry).
            toast.error(errorText(err, 'Booking failed'));
        } finally {
            setSubmitting(false);
        }
    };

    // Calendar generation helpers
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const linkTz = linkData?.timezone || tz;
    // #758: a chosen coach's month is still loading (the calendar is disabled).
    const coachDaysPending = !!coach && daysStatus !== 'failed'
        && !(daysStatus === 'ready' && serverDaysCoach === coach);

    const isDateAvailable = (date) => {
        if (!linkData) return false;
        // #756: once the server has answered for this month, it decides — a
        // day the coaches cannot take is disabled even when the link's own
        // weekdays would allow it. The client estimate below is the fallback
        // while `/days` loads or when it fails.
        if (daysStatus === 'ready' && serverDaysCoach === (coach || null)) return serverDays.has(formatDateStr(date));
        // #758 (audit F07): with a coach chosen, wait for THAT coach's answer —
        // the client estimate knows nothing about the coach, so it is used
        // only when /days failed.
        if (coach && daysStatus !== 'failed') return false;
        // #579: the weekday rule is the HOST's calendar, projected into the
        // viewing zone — a New York visitor's Sunday can hold a Manila host's
        // Monday-morning slots, and their Saturday holds nothing.
        if (!visitorDayHasHostSlots(formatDateStr(date), linkData.availability, linkTz, tz, linkData.duration)) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) return false;

        const maxDays = linkData.max_days_ahead || 60;
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + maxDays);
        if (date > maxDate) return false;

        return true;
    };

    const navigateMonth = (dir) => {
        const d = new Date(currentMonth);
        d.setMonth(d.getMonth() + dir);
        setCurrentMonth(d);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-center"><CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-4" /><p className="text-stone-500">{error}</p></div>
        </div>
    );

    const brandColor = linkData?.color || '#1a1a1a';
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const calendarDays = [];
    for (let i = 0; i < firstDay; i++) calendarDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calendarDays.push(new Date(year, month, d));

    // The calendar and times columns, shared by both layouts. Plain render
    // helpers (not components, not hooks), so the flag-off page renders exactly
    // the markup it did before #758 with the classes it is given.
    const renderCalendarColumn = (className) => (
        <div className={className}>
            {/* #539: invitee timezone — browser-detected
                default, manual override re-requests slots. */}
            <div className="mb-4">
                <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1" htmlFor="booking-timezone">Timezone</Label>
                <TimezoneSelect id="booking-timezone" value={tz} onChange={changeTz} />
                {/* #579: the host's published window, in the host's zone; the
                    slots below are the same instants shown in `tz`. */}
                <p className="mt-1.5 text-xs text-stone-500" data-testid="host-hours">
                    <Globe className="inline w-3 h-3 mr-1 -mt-0.5" />
                    Host availability: {hostWindowLabel(linkData.availability)} ({linkTz})
                    {linkTz !== tz && <> · times below shown in {tz}</>}
                </p>
            </div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-stone-800">{MONTH_NAMES[month]} {year}</h3>
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigateMonth(-1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigateMonth(1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_NAMES.map(d => <div key={d} className="text-center text-xs font-medium text-stone-400 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" aria-busy={coachDaysPending || undefined}>
                {calendarDays.map((day, i) => {
                    if (!day) return <div key={`empty-${i}`} />;
                    const available = isDateAvailable(day);
                    const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                        <button key={day.getDate()} disabled={!available} onClick={() => { setSelectedDate(day); setSelectedSlot(null); setCoachNotice(''); }}
                            className={`h-10 w-full rounded-lg text-sm font-medium transition-all ${isSelected ? 'text-white' : available ? 'text-stone-800 hover:bg-stone-100' : 'text-stone-300 cursor-not-allowed'} ${isToday && !isSelected ? 'ring-1 ring-stone-300' : ''}`}
                            style={isSelected ? { backgroundColor: brandColor } : undefined}
                            data-testid={`cal-day-${day.getDate()}`}>
                            {day.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    // #758: with the picker on and no coach chosen, each time says how many
    // coaches are free then ("Available" when only a grandfathered non-coach
    // member is, who has no public card).
    const slotCount = (slot) => {
        if (!picker || coach) return null;
        const n = slotFreeCoaches(slot).length;
        return n > 0 ? plural(n, 'coach', 'coaches') : 'Available';
    };
    const selectionSummary = () => {
        if (!selectedSlot || !selectedDate) return '';
        const when = `${shortDate(selectedDate)}, ${formatSlotLabel(selectedSlot)}`;
        if (chosenCoach) return `${when} with ${chosenCoach.display_name}.`;
        const n = slotFreeCoaches(selectedSlot).length;
        if (n === 0) return `${when}. You'll meet an available team member.`;
        if (n === 1) return `${when}. You'll meet the one free coach, confirmed on the next screen.`;
        return `${when}. You'll meet one of ${n} free coaches, confirmed on the next screen.`;
    };

    const renderTimesColumn = (className) => (
        <div className={className}>
            {!selectedDate ? (
                <div className="flex-1 flex items-center justify-center text-stone-400 text-sm text-center">
                    <div><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Select a date to see available times</p></div>
                </div>
            ) : slotsLoading ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : (
                <>
                    <p className="text-sm font-medium text-stone-700 mb-1">
                        {selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    <p className="text-xs text-stone-400 mb-3" data-testid="slots-zone">Times in {tz}</p>
                    <div className="flex-1 overflow-y-auto space-y-1.5 max-h-80">
                        {visibleSlots.length === 0 ? (
                            <p className="text-sm text-stone-400 text-center py-8">
                                {chosenCoach ? `${chosenCoach.display_name} has no times on this day` : 'No available times'}
                            </p>
                        ) : visibleSlots.map((slot, i) => {
                            // #579: slot.start_utc is the
                            // instant, labelled in `tz`;
                            // slot.start is the NAIVE
                            // wall-clock string IN `tz`
                            // (selection identity).
                            const timeStr = formatSlotLabel(slot);
                            const isSelected = selectedSlot?.start === slot.start;
                            const count = slotCount(slot);
                            return (
                                <button key={i} onClick={() => chooseSlot(slot)}
                                    className={`w-full py-2.5 px-3 rounded-lg text-sm font-medium border transition-all ${isSelected ? 'text-white border-transparent' : 'text-stone-700 border-stone-200 hover:border-stone-400'}${count ? ' flex items-center justify-between gap-2' : ''}`}
                                    style={isSelected ? { backgroundColor: brandColor, borderColor: brandColor } : undefined}
                                    aria-pressed={picker ? isSelected : undefined}
                                    data-testid={`slot-${i}`}>
                                    {count ? (
                                        <>
                                            <span>{timeStr}</span>
                                            {/* Inline spans concatenate in the accessible
                                                name ("10:00 AM3 coaches"); separate them. */}
                                            <span className="sr-only">, </span>
                                            <span className={`text-xs font-normal ${isSelected ? 'text-white/80' : 'text-stone-500'}`} data-testid={`slot-count-${i}`}>{count}</span>
                                        </>
                                    ) : timeStr}
                                </button>
                            );
                        })}
                    </div>
                    {picker ? (
                        <div className="sticky bottom-0 bg-white pt-3 pb-1" data-testid="continue-bar">
                            <p className="text-xs text-stone-600" aria-live="polite" data-testid="selection-summary">{selectionSummary()}</p>
                            {selectedSlot && (
                                <Button className="mt-2 w-full text-white" style={{ backgroundColor: brandColor }}
                                    onClick={() => setStep('form')} data-testid="continue-to-form">
                                    Continue
                                </Button>
                            )}
                        </div>
                    ) : selectedSlot && (
                        <Button className="mt-3 w-full text-white" style={{ backgroundColor: brandColor }}
                            onClick={() => setStep('form')} data-testid="continue-to-form">
                            Continue
                        </Button>
                    )}
                </>
            )}
        </div>
    );

    // Confirmed step
    if (step === 'confirmed' && confirmation) {
        const manageUrl = safeHttpUrl(confirmation.manage_url);
        return (
            <div className={`${isEmbed ? '' : 'min-h-screen'} flex items-center justify-center bg-white p-6`}>
                <div className="w-full max-w-md text-center" data-testid="booking-confirmed">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: brandColor + '15' }}>
                        <CheckCircle className="w-8 h-8" style={{ color: brandColor }} />
                    </div>
                    <h2 className="text-xl font-bold text-stone-900 mb-2">Meeting Scheduled!</h2>
                    <p className="text-sm text-stone-500 mb-6">{linkData.confirmation_message || 'Your meeting has been scheduled.'}</p>
                    <div className="bg-stone-50 rounded-xl p-4 text-left space-y-2 mb-4">
                        <p className="text-sm"><strong>What:</strong> {linkData.name}</p>
                        <p className="text-sm" data-testid="booking-when">
                            <strong>When:</strong>{" "}
                            {whenLineText(confirmation.start_time, zoneLabelAt(
                                confirmation.guest_timezone,
                                // The fallback covers a response with no start_utc: the naive
                                // wall-clock in the guest's zone is the same instant.
                                confirmation.start_utc || naiveInZone(confirmation.start_time, confirmation.guest_timezone),
                            ))}
                        </p>
                        {/* #758: a page that showed the coach picker shows WHO the
                            guest meets as the public coach card (photo, name,
                            title); every other page keeps the "With:" line. */}
                        {picker && confirmation.assigned_coach?.display_name ? (
                            <div className="pt-1" data-testid="booking-coach-card">
                                <CoachAvatar size="md" photoUrl={confirmation.assigned_coach.photo_url}
                                    name={confirmation.assigned_coach.display_name} title={confirmation.assigned_coach.title} />
                            </div>
                        ) : confirmation.assigned_user_name && <p className="text-sm"><strong>With:</strong> {confirmation.assigned_user_name}</p>}
                        {confirmation.meet_link && (
                            <a href={confirmation.meet_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-sm font-medium rounded-lg text-white" style={{ backgroundColor: brandColor }}>
                                {/* #755: a coach's pasted static link can be any provider
                                    (a Zoom URL on a "Google Meet" link), so it gets the
                                    neutral label. */}
                                <Video className="w-4 h-4" /> {joinButtonLabel(confirmation.meeting_link_source === 'static' ? null : linkData.location)}
                            </a>
                        )}
                    </div>
                    {confirmation.calendar_error ? (
                        // A09: the booking survived, the calendar write did not.
                        // Never claim an invite went out that never did.
                        <p
                            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-left"
                            data-testid="booking-calendar-error"
                        >
                            {calendarErrorNotice(confirmation.calendar_error, confirmation.assigned_user_name, { hasMeetingLink: !!confirmation.meet_link })}
                        </p>
                    ) : (
                        <p className="text-xs text-stone-400" data-testid="booking-invite-sent">A calendar invite has been sent to {confirmation.guest_email}</p>
                    )}
                    {/* #791 E34: the guest's way to cancel is the absolute `manage_url`, the
                        same string the confirmation email carries. The idempotent re-POST
                        withholds it, so that response points at the email instead. In embed
                        mode the manage page refuses to be framed, so it opens in a new tab. */}
                    <div className="mt-5 border-t border-stone-200 pt-3" data-testid="booking-manage">
                        {manageUrl ? (
                            <p className="text-sm text-stone-600">
                                Need to cancel?{' '}
                                <a href={manageUrl}
                                    {...(isEmbed ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    className="inline-flex min-h-11 items-center rounded font-medium text-stone-900 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                                    data-testid="booking-manage-link">
                                    Cancel booking
                                </a>
                            </p>
                        ) : (
                            <p className="text-sm text-stone-600" data-testid="booking-manage-hint">
                                To cancel, use the link in your confirmation email.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white flex items-center justify-center p-4`}>
            <div className={`w-full ${picker ? 'max-w-6xl' : 'max-w-3xl'}`} data-testid="public-booking-page">
                {/* Header */}
                <div className="text-center mb-6">
                    {linkData.org_name && <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-1">{linkData.org_name}</p>}
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{linkData.name}</h1>
                    {linkData.description && <p className="text-sm text-stone-500 mt-1 max-w-lg mx-auto">{linkData.description}</p>}
                    <div className="flex items-center justify-center gap-4 mt-3 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {linkData.duration} min</span>
                        {isConferenceLocation(linkData.location) && <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> {locationLabel(linkData.location)}</span>}
                        {linkData.location === 'phone' && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Phone</span>}
                        {linkData.booking_type === 'round_robin' && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Team</span>}
                    </div>
                </div>

                {/* #758: overflow-clip (not -hidden) with the picker, so the
                    sticky Continue can stick — overflow:hidden makes the card a
                    scroll container and pins sticky to it. */}
                <div className={`bg-white border border-stone-200 rounded-2xl ${picker ? 'overflow-clip' : 'overflow-hidden'} shadow-sm`}>
                    {step === 'calendar' ? (
                        picker ? (
                            // #758: the coach picker layout. ≥1024px (lg): one grid,
                            // coaches | calendar | times, every child min-w-0 (fr
                            // columns overflow otherwise). 768–1023px (md): coach
                            // chips above today's calendar | times pair. <768px and
                            // embeds: stacked, with a sticky Continue.
                            <div data-testid="coach-choice-layout">
                                <SelectionBar coach={chosenCoach} date={selectedDate} slot={selectedSlot}
                                    labels={{
                                        coach: chosenCoach?.display_name,
                                        date: selectedDate ? shortDate(selectedDate) : '',
                                        time: selectedSlot ? formatSlotLabel(selectedSlot) : '',
                                    }}
                                    onRemove={removeChoice} onReset={resetChoices} />
                                {/* Audit F08: the 409 message, announced and describing
                                    the coach list that receives focus. */}
                                <p id="coach-unavailable-notice" role="status" data-testid="coach-unavailable-notice"
                                    className={coachNotice ? 'px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-100' : 'sr-only'}>
                                    {coachNotice}
                                </p>
                                <div className="lg:hidden px-4 py-3 border-b border-stone-100 min-w-0">
                                    <CoachPicker ref={chipsPickerRef} layout="chips" coaches={coaches} value={coach} rows={coachRows.rows}
                                        anyHint={coachRows.anyHint} onChange={pickCoach} brandColor={brandColor}
                                        describedBy={coachNotice ? 'coach-unavailable-notice' : undefined} />
                                </div>
                                <div className="md:flex lg:grid lg:grid-cols-[320px_minmax(0,1fr)_300px]">
                                    <div className="hidden lg:block min-w-0 p-5 border-r border-stone-100">
                                        <CoachPicker ref={listPickerRef} layout="list" coaches={coaches} value={coach} rows={coachRows.rows}
                                            anyHint={coachRows.anyHint} onChange={pickCoach} brandColor={brandColor}
                                            describedBy={coachNotice ? 'coach-unavailable-notice' : undefined} />
                                    </div>
                                    {renderCalendarColumn('min-w-0 flex-1 p-5 md:border-r border-stone-100')}
                                    {renderTimesColumn('min-w-0 p-5 flex flex-col md:w-64 lg:w-auto')}
                                </div>
                            </div>
                        ) : (
                        <div className="md:flex">
                            {renderCalendarColumn('flex-1 p-5 border-r border-stone-100')}
                            {renderTimesColumn('w-64 p-5 flex flex-col')}
                        </div>
                        )
                    ) : step === 'form' ? (
                        <div className="p-6 max-w-md mx-auto" data-testid="booking-form">
                            <button onClick={() => setStep('calendar')} className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-4">
                                <ChevronLeft className="w-4 h-4" /> Back
                            </button>
                            <div className="bg-stone-50 rounded-lg p-3 mb-5 text-sm">
                                <p className="font-medium text-stone-800">{linkData.name}</p>
                                <p className="text-stone-500">{selectedDate?.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} at {formatSlotLabel(selectedSlot)} ({tz})</p>
                                {chosenCoach && <p className="text-stone-500" data-testid="form-coach">With {chosenCoach.display_name}</p>}
                                <p className="text-stone-400">{linkData.duration} min{isConferenceLocation(linkData.location) ? ` · ${locationLabel(linkData.location)}` : ''}</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Name *</Label>
                                    <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Your full name" className="mt-1" data-testid="booking-name" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Email *</Label>
                                    <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="you@company.com" className="mt-1" data-testid="booking-email" />
                                </div>
                                {(linkData.custom_questions || []).map((q, i) => (
                                    <div key={i}>
                                        <Label className="text-xs font-semibold text-stone-500 uppercase">{q.label} {q.required && '*'}</Label>
                                        <Input value={answers[q.label] || ''} onChange={e => setAnswers({ ...answers, [q.label]: e.target.value })} className="mt-1" data-testid={`custom-q-${i}`} />
                                    </div>
                                ))}
                                <div>
                                    <Label className="text-xs font-semibold text-stone-500 uppercase">Notes (optional)</Label>
                                    <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Anything else we should know?" rows={2} className="mt-1" data-testid="booking-notes" />
                                </div>
                            </div>
                            <Button className="w-full mt-5 text-white" style={{ backgroundColor: brandColor }} onClick={handleBook} disabled={submitting} data-testid="confirm-booking-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                Confirm Booking
                            </Button>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                {!isEmbed && (
                    <p className="text-center text-xs text-stone-400 mt-6">
                        Powered by <span className="font-semibold text-stone-600">NurtureCRM</span>
                    </p>
                )}
            </div>
        </div>
    );
}
